import { Button } from "@/components/flexkit/Button";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Heading1, Subheading } from "@/components/podkit/typography/Headings";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { TrackLocations } from "@/hooks/use-segment";
import { useGetIntegration, useListIntegrationDefinitions } from "@/queries/integration-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { type FC, useCallback } from "react";
import { useParams } from "react-router-dom";

export const ConnectIntegrationPage: FC = () => {
    const { integrationId } = useParams<{ integrationId: string }>();
    useDocumentTitle("Connect Integration");

    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const { data: integration, isLoading: isLoadingIntegration, isError } = useGetIntegration(integrationId);
    const { data: definitions, isLoading: isLoadingDefinitions } = useListIntegrationDefinitions();
    const { data: organization, isLoading: isLoadingOrg } = useOrganization();

    const definition = definitions?.find((d) => d.id === integration?.integrationDefinitionId);
    const isLoading = isLoadingUser || isLoadingIntegration || isLoadingDefinitions;

    const handleConnect = useCallback(() => {
        if (!integration || !user) {
            return;
        }
        const authUrl = `/integrations/oauth/${integration.id}/authorize?principal=${encodeURIComponent(`user/${user.id}`)}`;
        window.location.assign(authUrl);
    }, [integration, user]);

    if (isLoading) {
        return <LoadingState />;
    }

    if (isError || !integration) {
        return (
            <div
                data-testid="connect-integration-error"
                className="flex size-full grow flex-col items-center justify-center px-1 py-4 md:px-8"
            >
                <div className="flex w-96 max-w-[65vw] flex-col items-center justify-start gap-4">
                    <Heading1>Integration not found</Heading1>
                    <Subheading className="text-center">
                        The integration you are trying to connect does not exist or you don&apos;t have access to it.
                    </Subheading>
                    <a href="/">
                        <Button variant="secondary">Go to Dashboard</Button>
                    </a>
                </div>
            </div>
        );
    }

    if (!integration.enabled) {
        return (
            <div
                data-testid="connect-integration-disabled"
                className="flex size-full grow flex-col items-center justify-center px-1 py-4 md:px-8"
            >
                <div className="flex w-96 max-w-[65vw] flex-col items-center justify-start gap-4">
                    <Heading1>Integration disabled</Heading1>
                    <Subheading className="text-center">
                        The <span className="font-bold">{definition?.name ?? "requested"}</span> integration is
                        currently disabled. Contact your organization admin to enable it.
                    </Subheading>
                    <a href="/">
                        <Button variant="secondary">Go to Dashboard</Button>
                    </a>
                </div>
            </div>
        );
    }

    if (user && integration.organizationId !== user.organizationId) {
        return (
            <div
                data-testid="connect-integration-wrong-org"
                className="flex size-full grow flex-col items-center justify-center px-1 py-4 md:px-8"
            >
                <div className="flex w-96 max-w-[65vw] flex-col items-center justify-start gap-4">
                    <Heading1>Wrong organization</Heading1>
                    <Subheading className="text-center">
                        This integration belongs to a different organization. Switch to the correct organization below.
                    </Subheading>
                    <OrgSwitcher
                        data-track-location={TrackLocations.ConnectIntegrationOrgSwitcher}
                        isLocked
                        hideKeyboardShortcuts
                        hideUserSettings
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="connect-integration-page"
            className="flex size-full grow flex-col items-center justify-center px-1 py-4 md:px-8"
        >
            <div className="flex w-96 max-w-[65vw] flex-col items-center justify-start gap-4">
                <Heading1>{`Connect ${definition?.name ?? "integration"} integration`}</Heading1>
                <Subheading className="text-center">
                    <span>
                        {isLoadingOrg ? (
                            <span className="inline-block h-5 w-48 animate-pulse rounded bg-surface-muted" />
                        ) : (
                            <span>
                                Connect your{" "}
                                <span className="font-bold">{definition?.name ?? "integration"}</span>{" "}
                                account to the <span className="font-bold">{organization?.name}</span>{" "}
                                organization.
                            </span>
                        )}
                    </span>
                </Subheading>
                <OrgSwitcher
                    data-track-location={TrackLocations.ConnectIntegrationOrgSwitcher}
                    isLocked
                    hideKeyboardShortcuts
                    hideUserSettings
                />
                <Button
                    variant="primary"
                    onClick={handleConnect}
                    disabled={isLoadingOrg}
                    data-testid="connect-integration-button"
                    data-tracking-id="connect-integration"
                >
                    Connect
                </Button>
            </div>
        </div>
    );
};
